import React from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  AssessmentQuestionInfo,
  UserAssessment,
  UserAssessmentResponse,
} from '../../../../../utils/types/assessment_types';
import { QuestionArea } from './question_area/question_area';

type QuestionProps = {
  selectedAssessment: UserAssessment | undefined;
  videoElementRef: React.RefObject<HTMLVideoElement>;
  currentQuestion: AssessmentQuestionInfo | undefined;
  currentAnswer: string | File | undefined;
  setCurrentAnswer: (value: string | File | undefined) => void;
  nextQuestion: (
    sampleBlob?: Blob[],
    sampleAudioBlob?: Blob[],
    skip?: boolean,
  ) => Promise<void>;
  recordedVideo: string | undefined;
  setRecordedVideo: (value: string | undefined) => void;
  timeRemaining: number | undefined;
  isPractice: boolean | undefined;
  questionNumber: number | undefined;
  recordedAudio?: string | undefined;
  questionCount: number;
  loading: boolean;
  progress: number;
  uploadStatus: 'initial' | 'loading' | 'success' | 'error';
  setUploadStatus: (
    value:
      | ((
          prevState: 'initial' | 'loading' | 'success' | 'error',
        ) => 'initial' | 'loading' | 'success' | 'error')
      | 'initial'
      | 'loading'
      | 'success'
      | 'error',
  ) => void;
  handleQuestionSubmission: (
    video: File,
    timeExpired: boolean,
  ) => Promise<void>;
  responseUserAssessment: UserAssessmentResponse | undefined;
  handleNavigationResult: (navResponse: any) => void;
  setQuestionCount: (value: ((prevState: number) => number) | number) => void;
  submitNoFile: (timeExpired: boolean) => Promise<void>;
  setRecordedAudio: (
    value:
      | ((prevState: string | undefined) => string | undefined)
      | string
      | undefined,
  ) => void;
};

export const Question: React.FC<QuestionProps> = ({
  selectedAssessment,
  nextQuestion,
  currentQuestion,
  currentAnswer,
  setCurrentAnswer,
  recordedVideo,
  setRecordedVideo,
  timeRemaining,
  isPractice,
  questionNumber,
  recordedAudio,
  questionCount,
  loading,
  progress,
  uploadStatus,
  setUploadStatus,
  handleQuestionSubmission,
  responseUserAssessment,
  handleNavigationResult,
  setQuestionCount,
  submitNoFile,
  setRecordedAudio,
}) => {
  const totalQuestions = selectedAssessment?.assessment?.totalScore ?? 8;
  const isBehavioral = currentQuestion?.questionType === 'Behavioral';

  // Stable display states to prevent unmounting/flickering during fetches
  const [displayQuestion, setDisplayQuestion] = React.useState(currentQuestion);
  const [displayNumber, setDisplayNumber] = React.useState(questionNumber);
  const [isVisible, setIsVisible] = React.useState(true);

  React.useEffect(() => {
    // When a new question is ready, or current question is cleared (loading), run transition
    if (currentQuestion && currentQuestion.id !== displayQuestion?.id) {
      setIsVisible(false);
      const timer = setTimeout(() => {
        setDisplayQuestion(currentQuestion);
        setDisplayNumber(questionNumber);
        // Reset response states for the new question during the fade-out
        setRecordedVideo(undefined);
        setRecordedAudio(undefined);
        setCurrentAnswer(undefined);
        setIsVisible(true);
      }, 300);
      return () => clearTimeout(timer);
    }
    // If no question initially, but one arrives
    if (currentQuestion && !displayQuestion) {
      setDisplayQuestion(currentQuestion);
      setDisplayNumber(questionNumber);
    }
    // If we transition to a loading state (no current question), fade out
    if (!currentQuestion && isVisible) {
      setIsVisible(false);
    }
  }, [currentQuestion?.id, displayQuestion?.id, questionNumber]);

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        maxWidth: 1280,
        mx: 'auto',
        bgcolor: 'grey.50',
        p: { xs: 1, md: 2 },
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          height: '100%',
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 1.5, md: 2 },
          overflowY: 'auto',
          pr: 0.5,
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 300ms ease-in-out',
        }}
      >
        {/* Question Header */}
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: { xs: 2, md: 3 },
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                label={`Question ${displayNumber}`}
                color="primary"
                size="small"
                sx={{ fontWeight: 600, fontSize: { xs: 11, md: 12 } }}
              />
              <Chip
                label={
                  ['Classic', 'Modern'].includes(displayQuestion?.questionType ?? '')
                    ? 'Skill'
                    : displayQuestion?.questionType || 'Video Response'
                }
                color="success"
                size="small"
                sx={{ fontWeight: 600, fontSize: { xs: 11, md: 12 } }}
              />
            </Stack>
          </Stack>
        </Paper>

        {/* Question Content */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: { xs: 2, md: 3 },
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          {displayQuestion?.attribute && (
            <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1.5, mb: 1.5, textAlign: 'center' }}>
              <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                These are questions pertaining to your{' '}
                <Typography component="span" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {displayQuestion.attribute}
                </Typography>{' '}
                skills
              </Typography>
            </Box>
          )}

          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1.5, mb: 2 }}>
            <Typography variant="h6" sx={{ fontSize: { xs: 14, md: 18 }, fontWeight: 600, color: 'text.primary' }}>
              {uploadStatus === 'loading' ? (
                <Typography component="span" sx={{ color: 'text.secondary', animation: 'pulse 1.5s infinite' }}>
                  Please wait while the system processes your previous answer...
                </Typography>
              ) : (
                displayQuestion?.question
              )}
            </Typography>
          </Box>

          <Box sx={{ p: { xs: 0.5, md: 1 } }}>
            {displayQuestion && (
              <QuestionArea
                currentAnswer={currentAnswer}
                questionType={displayQuestion.questionType || 'Classic'}
                setCurrentAnswer={setCurrentAnswer}
                recordedVideo={recordedVideo}
                setRecordedVideo={setRecordedVideo}
                currentQuestion={displayQuestion}
                timeRemaining={timeRemaining}
                setRecordedAudio={setRecordedAudio}
                submissionDisabled={currentAnswer === undefined}
                submitAnswer={nextQuestion}
                loading={loading}
                uploadStatus={uploadStatus}
                setUploadStatus={setUploadStatus}
                handleQuestionSubmission={handleQuestionSubmission}
                responseUserAssessment={responseUserAssessment}
                handleNavigationResult={handleNavigationResult}
                questionCount={questionCount}
                setQuestionCount={setQuestionCount}
                submitNoFile={submitNoFile}
                totalQuestions={totalQuestions}
                isBehavioral={isBehavioral}
              />
            )}
          </Box>

          {uploadStatus === 'loading' && (
            <Box sx={{ position: 'relative', my: 2.5, width: '100%' }}>
              <LinearProgress
                variant="determinate"
                value={Math.floor(progress)}
                sx={{ height: 24, borderRadius: 1.5 }}
              />
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  color: 'common.white',
                  pointerEvents: 'none',
                }}
              >
                Uploading Answer {Math.floor(progress)}%
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

